import { useMemo, useState } from 'react';

import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  ExternalLink,
  MinusCircle,
  MousePointerClick,
  Webhook,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useWorkflowExecutionLog } from '@/hooks/useWorkflows';
import { formatDateTime } from '@/utils/format';

import type { ExecutionNodeResult } from '@/lib/api/workflows.api';

const STATUS_STYLES: Record<string, { icon: typeof CheckCircle2; dot: string; text: string }> = {
  success: { icon: CheckCircle2, dot: 'bg-green-500', text: 'text-green-600 dark:text-green-400' },
  error: { icon: AlertCircle, dot: 'bg-red-500', text: 'text-red-600 dark:text-red-400' },
  skipped: { icon: MinusCircle, dot: 'bg-gray-400', text: 'text-muted-foreground' },
};

const statusStyle = (status: string) => STATUS_STYLES[status] ?? STATUS_STYLES.skipped;

const formatDuration = (ms?: number) => {
  if (ms === undefined || ms === null) return null;
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(ms < 10_000 ? 2 : 1)}s`;
};

/** Elapsed wall-clock between the run's start and finish. */
const totalDuration = (startedAt?: string, completedAt?: string) => {
  if (!startedAt || !completedAt) return null;
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  return Number.isFinite(ms) && ms >= 0 ? formatDuration(ms) : null;
};

const asText = (value: unknown) =>
  typeof value === 'string' ? value : JSON.stringify(value, null, 2);

const JsonBlock = ({ label, value }: { label: string; value: unknown }) => {
  const text = asText(value);
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <div className="text-muted-foreground text-[10.5px] font-medium tracking-wide uppercase">
          {label}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-[11px]"
          onClick={() => navigator.clipboard?.writeText(text)}
        >
          <Copy className="mr-1 h-3 w-3" />
          Copy
        </Button>
      </div>
      <pre className="bg-muted max-h-80 overflow-auto rounded-md p-3 text-xs leading-relaxed">
        {text}
      </pre>
    </div>
  );
};

const StepRow = ({
  result,
  index,
  label,
  open,
  onToggle,
}: {
  result: ExecutionNodeResult;
  index: number;
  label?: string;
  open: boolean;
  onToggle: () => void;
}) => {
  const style = statusStyle(result.status);
  const Icon = style.icon;
  const duration = formatDuration(result.duration_ms);
  const hasDetail =
    result.message !== undefined ||
    result.request_payload !== undefined ||
    result.response_data !== undefined;

  return (
    <div className="border-border border-b last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        className="hover:bg-muted/50 flex w-full items-center gap-3 px-4 py-3 text-left transition-colors"
      >
        {open ? (
          <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
        )}

        <span className="text-muted-foreground w-6 shrink-0 text-xs tabular-nums">{index + 1}</span>

        <Icon className={`h-4 w-4 shrink-0 ${style.text}`} />

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{label ?? result.nodeId}</span>
          <span className="text-muted-foreground block truncate text-xs">
            {result.nodeType}
            {label ? ` · ${result.nodeId}` : ''}
          </span>
        </span>

        {result.branch && (
          <Badge variant="outline" className="shrink-0 text-[11px]">
            {result.branch}
          </Badge>
        )}
        {duration && (
          <span className="text-muted-foreground shrink-0 text-xs tabular-nums">{duration}</span>
        )}
      </button>

      {open && (
        <div className="bg-muted/30 space-y-3 px-4 pt-1 pb-4 pl-[4.25rem]">
          {!hasDetail && (
            <div className="text-muted-foreground text-xs">
              No output recorded for this step — it ran and reported {result.status}.
            </div>
          )}
          {result.message !== undefined && (
            <div>
              <div className="text-muted-foreground mb-1 text-[10.5px] font-medium tracking-wide uppercase">
                Message
              </div>
              <div className={`text-sm ${result.status === 'error' ? style.text : ''}`}>
                {result.message}
              </div>
            </div>
          )}
          {result.request_payload !== undefined && (
            <JsonBlock label="Request" value={result.request_payload} />
          )}
          {result.response_data !== undefined && (
            <JsonBlock label="Response" value={result.response_data} />
          )}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <div className="text-muted-foreground text-[10.5px] font-medium tracking-wide uppercase">
      {label}
    </div>
    <div className="mt-1 text-sm font-semibold">{value}</div>
  </div>
);

export const ExecutionDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useWorkflowExecutionLog(id);
  const [openSteps, setOpenSteps] = useState<Set<string>>(new Set());

  usePageTitle(data ? `${data.workflow_name} — Execution` : 'Execution');

  const results = useMemo(() => data?.execution_log?.results ?? [], [data]);

  // Node ids alone are meaningless in the UI, so pair each result with its
  // label from the workflow graph. A node deleted since the run has none.
  const labelByNodeId = useMemo(() => {
    const map = new Map<string, string>();
    for (const node of data?.workflow?.post_call_workflow?.nodes ?? []) {
      const label = (node.data as { label?: string })?.label;
      if (label) map.set(node.id, label);
    }
    return map;
  }, [data]);

  const toggle = (key: string) =>
    setOpenSteps((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const allKeys = results.map((r, i) => `${r.nodeId}-${i}`);
  const allOpen = allKeys.length > 0 && allKeys.every((k) => openSteps.has(k));

  if (isLoading) {
    return <div className="text-muted-foreground p-6 text-sm">Loading execution...</div>;
  }

  if (!data) {
    return (
      <div className="space-y-4 p-6">
        <Link to="/workflows/history">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to history
          </Button>
        </Link>
        <div className="text-muted-foreground text-sm">Execution not found.</div>
      </div>
    );
  }

  const log = data.execution_log ?? {};
  const failed = data.errors > 0 || data.status === 'failed';
  const elapsed = totalDuration(log.started_at, log.completed_at);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <Link to="/workflows/history">
            <Button variant="ghost" size="sm" className="mb-2 -ml-2">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to history
            </Button>
          </Link>
          <h1 className="truncate text-xl font-semibold">{data.workflow_name}</h1>
          <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2 text-sm">
            <Badge
              variant={failed ? 'destructive' : 'default'}
              className={failed ? '' : 'bg-green-600 hover:bg-green-600/80'}
            >
              {data.status}
            </Badge>
            <Badge variant="outline">{data.trigger_type}</Badge>
            {log.dry_run && <Badge variant="secondary">dry run</Badge>}
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {formatDateTime(data.created_at)}
            </span>
          </div>
        </div>
        {data.workflow && (
          <Link to={`/workflows/${data.workflow_id}`}>
            <Button variant="outline" size="sm">
              Open workflow
            </Button>
          </Link>
        )}
      </div>

      <Card>
        <CardContent className="grid grid-cols-2 gap-6 pt-6 sm:grid-cols-4">
          <Stat label="Steps run" value={`${data.executed_nodes} / ${data.total_nodes}`} />
          <Stat
            label="Errors"
            value={
              <span className={data.errors > 0 ? 'text-red-600 dark:text-red-400' : undefined}>
                {data.errors}
              </span>
            }
          />
          <Stat label="Duration" value={elapsed ?? '—'} />
          <Stat
            label="Source"
            value={<span className="font-mono text-xs">{data.source_id ?? '—'}</span>}
          />
        </CardContent>
      </Card>

      {log.trigger_payload && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Webhook className="text-muted-foreground h-5 w-5" />
              <CardTitle className="text-sm">Trigger Payload</CardTitle>
            </div>
            <CardDescription>What was sent to the workflow to start this run</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {log.trigger_payload.body !== undefined && (
              <JsonBlock label="Body" value={log.trigger_payload.body} />
            )}
            {log.trigger_payload.query && Object.keys(log.trigger_payload.query).length > 0 && (
              <JsonBlock label="Query" value={log.trigger_payload.query} />
            )}
            {log.trigger_payload.headers && (
              <JsonBlock label="Headers" value={log.trigger_payload.headers} />
            )}
            <div className="text-muted-foreground flex flex-wrap gap-4 text-xs">
              {log.trigger_payload.method && <span>Method {log.trigger_payload.method}</span>}
              {log.trigger_payload.ip && <span>From {log.trigger_payload.ip}</span>}
              {log.trigger_payload.received_at && (
                <span>Received {formatDateTime(log.trigger_payload.received_at)}</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {data.tracked_links?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <MousePointerClick className="text-muted-foreground h-5 w-5" />
              <CardTitle className="text-sm">Link Tracking</CardTitle>
            </div>
            <CardDescription>
              Links this run generated, and whether they were opened
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {data.tracked_links.map((link) => (
                <div
                  key={link.id}
                  className="rounded-lg border bg-white p-4 shadow-sm dark:bg-gray-900"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold tracking-wider text-blue-800 uppercase dark:bg-blue-900 dark:text-blue-300">
                      {link.linkType.replace(/_/g, ' ')}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                        link.clicked
                          ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                      }`}
                    >
                      {link.clicked ? 'Clicked' : 'Pending'}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <div>
                      <p className="text-muted-foreground mb-1 text-[10px] font-bold tracking-wider uppercase">
                        Original URL
                      </p>
                      <a
                        href={link.originalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary flex items-start gap-1 overflow-hidden text-xs break-all hover:underline"
                      >
                        <span className="min-w-0 flex-1">{link.originalUrl}</span>
                        <ExternalLink className="mt-0.5 h-3 w-3 flex-shrink-0" />
                      </a>
                    </div>

                    <div className="flex flex-col gap-1.5 pt-1">
                      {link.clicked && link.clickedAt && (
                        <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          <span>Clicked {formatDateTime(link.clickedAt)}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-[10px] text-gray-400">
                        <Clock className="h-3 w-3" />
                        <span>Created {formatDateTime(link.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Steps</CardTitle>
          {results.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpenSteps(allOpen ? new Set() : new Set(allKeys))}
            >
              {allOpen ? 'Collapse all' : 'Expand all'}
            </Button>
          )}
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {results.length === 0 ? (
            <div className="text-muted-foreground px-6 pb-6 text-sm">
              This run recorded no steps.
            </div>
          ) : (
            <div className="border-border border-t">
              {results.map((result, index) => {
                const key = `${result.nodeId}-${index}`;
                return (
                  <StepRow
                    key={key}
                    result={result}
                    index={index}
                    label={labelByNodeId.get(result.nodeId)}
                    open={openSteps.has(key)}
                    onToggle={() => toggle(key)}
                  />
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {log.returnValue !== undefined && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Returned to caller</CardTitle>
          </CardHeader>
          <CardContent>
            <JsonBlock label="Return value" value={log.returnValue} />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Raw log</CardTitle>
        </CardHeader>
        <CardContent>
          <JsonBlock label="execution_log" value={log} />
        </CardContent>
      </Card>
    </div>
  );
};
